/**
 * Handles hashtags in URLs and links
 * - Scrolls to hashtag element after content loads if present in URL
 * - Intercepts links with hashtags and scrolls to target elements
 * - Uses CSS selectors (e.g., #contact matches .contact class)
 */

/**
 * Find element by CSS selector
 * Converts hash to CSS class selector (e.g., "contact" becomes ".contact")
 * @param {string} selector - The selector from hash (e.g., "contact")
 * @returns {HTMLElement|null} The matching element or null
 */
function findElementBySelector(selector) {
  if (!selector) return null;

  // Convert hash to class selector (e.g., "contact" -> ".contact")
  const classSelector = '.' + selector;

  // Find first element matching the class selector
  const element = document.querySelector(classSelector);

  return element || null;
}

/**
 * Scroll to element with smooth behavior and offset for fixed headers
 * @param {HTMLElement} element - The element to scroll to
 */
function scrollToElement(element) {
  if (!element) return;

  // Calculate offset for fixed headers (adjust if needed)
  const headerOffset = 100;
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
}

/**
 * Handle hash from URL or hash string
 * @param {string} hash - The hash (e.g., "#contact" or "contact")
 */
function handleHash(hash) {
  if (!hash) return;

  // Remove # if present
  const selector = hash.replace(/^#/, '');
  if (!selector) return;

  // Find element using CSS selector (class)
  const element = findElementBySelector(selector);

  if (element) {
    // Use setTimeout to ensure content is fully rendered
    setTimeout(() => {
      scrollToElement(element);
    }, 100);
  }
}

/**
 * Handle hash in current URL
 */
function handleUrlHash() {
  const hash = window.location.hash;
  if (hash) {
    handleHash(hash);
  }
}

// Handle hash on initial page load
document.addEventListener('DOMContentLoaded', () => {
  handleUrlHash();
});

// Handle hash after Alpine AJAX content loads
document.addEventListener('alpine-ajax:loaded', () => {
  handleUrlHash();
});

// Also handle on window load (for delayed content)
window.addEventListener('load', () => {
  handleUrlHash();
});

// Intercept clicks on links with hashtags
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href*="#"]');
  if (!link) return;

  const href = link.getAttribute('href');
  if (!href) return;

  // Check if link contains a hash
  const hashMatch = href.match(/#(.+)$/);
  if (!hashMatch) return;

  const hash = hashMatch[1];

  // Check if it's a same-page link
  // - Links starting with # are same-page (e.g., #contact)
  // - Links starting with /# are same-page (e.g., /#contact)
  // - External links (http/https) should navigate normally
  // - Links with path before hash should navigate normally (browser will handle)
  const isSamePage = href.startsWith('#') || href === '/#' + hash;

  if (isSamePage) {
    e.preventDefault();
    handleHash(hash);

    // Update URL without triggering scroll
    const newUrl = window.location.pathname + (window.location.search || '') + '#' + hash;
    window.history.pushState(null, '', newUrl);
  }
});

// Handle browser back/forward buttons
window.addEventListener('popstate', () => {
  handleUrlHash();
});

// Handle hash change events
window.addEventListener('hashchange', () => {
  handleUrlHash();
});